import {
  useState,
  useCallback,
  useMemo,
  useDeferredValue,
  useEffect,
  useRef,
} from 'react'
import type { JSX } from 'react'

import { Command, useCommandState } from 'cmdk'

import { Flex, Kbd, SearchIcon, Text } from '@buffer-mono/popcorn'

import type {
  CommandBarAction,
  ActionSection,
  FrecencyStorage,
} from './actions/types'
import { CommandBarEmpty } from './CommandBarEmpty'
import { CommandBarItem } from './CommandBarItem'
import { CommandBarLoading } from './CommandBarLoading'
import { SECTION_ORDER, SECTION_LABELS } from './constants'
import { filterAndScoreActions } from './utils/search'

import styles from './CommandBar.module.css'

/**
 * Inner input component that tracks the active cmdk item and sets
 * `aria-activedescendant` so screen readers announce highlighted options.
 * Must be rendered inside `<Command>` for `useCommandState` to work.
 */
function CommandBarInput({
  value,
  onValueChange,
}: {
  value: string
  onValueChange: (value: string) => void
}): JSX.Element {
  const highlightedValue = useCommandState((state) => state.value)
  const inputRef = useRef<HTMLInputElement>(null)

  // cmdk's Command.Input sets aria-activedescendant internally but only
  // computes it once on mount (useMemo with [] deps). We correct this by
  // updating the attribute directly on the DOM whenever the highlighted
  // item changes.
  useEffect(() => {
    const input = inputRef.current
    if (!input) return

    if (!highlightedValue) {
      input.removeAttribute('aria-activedescendant')
      return
    }

    const escaped = globalThis.CSS?.escape(highlightedValue) ?? highlightedValue
    const activeItemId = document.querySelector(
      `[cmdk-item=""][data-value="${escaped}"]`,
    )?.id
    if (activeItemId) {
      input.setAttribute('aria-activedescendant', activeItemId)
    } else {
      input.removeAttribute('aria-activedescendant')
    }
  }, [highlightedValue])

  return (
    <div className={styles.inputWrapper}>
      <SearchIcon
        size="medium"
        className={styles.inputIcon}
        aria-hidden="true"
      />
      <Command.Input
        ref={inputRef}
        placeholder="Search channels, pages..."
        aria-label="Search for channels and pages"
        value={value}
        onValueChange={onValueChange}
        className={styles.input}
      />
      <Flex gap="xs" className={styles.shortcutHint}>
        <Kbd>mod</Kbd>
        <Kbd>K</Kbd>
      </Flex>
    </div>
  )
}

/**
 * Props for the CommandBarContent component.
 */
interface CommandBarContentProps {
  /** List of available actions to display in the command bar */
  actions: CommandBarAction[]
  /** Whether actions are still loading */
  loading?: boolean
  /** Callback when an action is selected, receives the action and current search query */
  onSelectAction?: (action: CommandBarAction, searchQuery: string) => void
  /** Callback when the search query changes */
  onQueryChange?: (query: string) => void
  /** Frecency data for ranking actions by usage patterns */
  frecencyData?: FrecencyStorage
}

/**
 * The main content component for the command bar.
 * Renders a searchable list of actions grouped by section.
 *
 * @param props - Component props
 * @param props.actions - List of available actions to display
 * @param props.loading - Whether actions are still loading
 * @param props.onSelectAction - Callback when an action is selected
 * @param props.onQueryChange - Callback when search query changes
 * @param props.frecencyData - Frecency data for sorting actions
 * @returns The command bar content UI
 */
export function CommandBarContent({
  actions,
  loading = false,
  onSelectAction,
  onQueryChange,
  frecencyData = {},
}: CommandBarContentProps): JSX.Element {
  const [search, setSearch] = useState('')
  // Defer the search value to avoid expensive filtering on every keystroke
  const deferredSearch = useDeferredValue(search)

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearch(value)
      onQueryChange?.(value)
    },
    [onQueryChange],
  )

  const handleSelect = useCallback(
    (action: CommandBarAction) => {
      onSelectAction?.(action, search)
    },
    [onSelectAction, search],
  )

  const filteredActions = useMemo(() => {
    return filterAndScoreActions(deferredSearch, actions, frecencyData)
  }, [deferredSearch, actions, frecencyData])

  const groupedActions = useMemo(() => {
    const groups = new Map<ActionSection, CommandBarAction[]>()

    for (const action of filteredActions) {
      const section = action.section
      if (!groups.has(section)) {
        groups.set(section, [])
      }
      groups.get(section)?.push(action)
    }

    return groups
  }, [filteredActions])

  const orderedSections = useMemo(() => {
    return SECTION_ORDER.filter((section) => {
      const sectionActions = groupedActions.get(section)
      return sectionActions && sectionActions.length > 0
    })
  }, [groupedActions])

  return (
    <Command className={styles.command} shouldFilter={false} loop>
      <CommandBarInput value={search} onValueChange={handleSearchChange} />

      <Command.List className={styles.list}>
        {loading ? (
          <CommandBarLoading />
        ) : (
          <>
            <CommandBarEmpty query={search} />
            {orderedSections.map((section) => {
              const sectionActions = groupedActions.get(section) ?? []
              const showHeader = sectionActions.length >= 2

              return (
                <Command.Group
                  key={section}
                  heading={showHeader ? SECTION_LABELS[section] : undefined}
                  className={styles.group}
                >
                  {sectionActions.map((action) => (
                    <CommandBarItem
                      key={action.id}
                      action={action}
                      onSelect={handleSelect}
                    />
                  ))}
                </Command.Group>
              )
            })}
          </>
        )}
      </Command.List>

      <div className={styles.footer}>
        <span className={styles.footerHintItem}>
          <Kbd>↑</Kbd>
          <Kbd>↓</Kbd>
          <Text size="sm" color="subtle">
            Navigate
          </Text>
        </span>
        <span className={styles.footerHintItem}>
          <Kbd>↵</Kbd>
          <Text size="sm" color="subtle">
            Select
          </Text>
        </span>
        <span className={styles.footerHintItem}>
          <Kbd>Esc</Kbd>
          <Text size="sm" color="subtle">
            Close
          </Text>
        </span>
      </div>
    </Command>
  )
}
